import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { useSession } from '../../lib/auth/session.js';
import { formatMoney } from '../../lib/format.js';
import { parseCsvRows, parseRateSheet, type OwnRateSheet as Sheet, type ParsedRateSheet } from '@arcnet/contracts';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * What the client's own carrier charges them (0131, owner 26 September 2026): a sheet uploaded for
 * this trunk, used to show the carrier's cost on every call over it. Never charged by ArcNet.
 *
 * Read in the browser with the same parser the API validates against, so the preview is what is
 * stored. CSV, since every spreadsheet saves one; a carrier's title rows above the header are fine.
 */
export function OwnRateSheet({ tenantId, trunkId }: { tenantId: string; trunkId: string }) {
  const can = useSession((s) => s.can);
  const mayWrite = can('connectivity:write');
  const qc = useQueryClient();
  const base = `/v1/tenants/${tenantId}/own-trunks/${trunkId}/rates`;
  const sheet = useQuery({ queryKey: ['own-rates', trunkId], queryFn: () => api.get<Sheet | { deck_id: null }>(base) });
  const current = sheet.data && sheet.data.deck_id ? sheet.data as Sheet : null;

  const [file, setFile] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedRateSheet | { error: string } | null>(null);
  const upload = useMutation({
    mutationFn: () => api.post<Sheet>(base, { rules: (parsed as ParsedRateSheet).rules, ...(file ? { file_name: file } : {}) }),
    onSuccess: () => { setParsed(null); setFile(null); setAi(null); void qc.invalidateQueries({ queryKey: ['own-rates', trunkId] }); },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(base),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['own-rates', trunkId] }),
  });

  const [number, setNumber] = useState('');
  const [asked, setAsked] = useState('');
  const lookup = useQuery({
    queryKey: ['own-rates-lookup', trunkId, asked, current?.version],
    queryFn: () => api.get<{ number: string; found: boolean; currency?: string; one_minute?: string;
      rule?: { prefix: string; description: string | null; rate_per_minute: string; connection_fee: string; first_increment_sec: number; subsequent_increment_sec: number } | null }>(
      `${base}/lookup?number=${encodeURIComponent(asked)}`),
    enabled: !!current && asked.length >= 2,
  });

  /**
   * A CSV the browser can read is read here. Anything else -- a PDF, a spreadsheet, a CSV whose
   * columns are not recognisable -- goes to the API, which reads a spreadsheet directly and a PDF
   * with AI (carriers/ratesheetai.ts). Either way the answer comes back as a CSV for this preview.
   */
  const [ai, setAi] = useState<{ id: string; how: 'direct' | 'ai' | null; notes: string[]; currency: string | null; seconds: number; status: string; error: string | null } | null>(null);
  const [blob, setBlob] = useState<File | null>(null);
  const send = async (f: File, forceAi = false) => {
    setParsed(null);
    const data = await new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result).replace(/^data:[^,]*,/, ''));
      r.onerror = () => rej(new Error('The file could not be read.'));
      r.readAsDataURL(f);
    });
    try {
      const { id } = await api.post<{ id: string }>(`${base}/read`, { file_name: f.name, data, ...(forceAi ? { force_ai: true } : {}) });
      setAi({ id, how: null, notes: [], currency: null, seconds: 0, status: 'reading', error: null });
    } catch (e) {
      setParsed({ error: e instanceof Error ? e.message : 'The file could not be sent.' });
    }
  };
  const job = useQuery({
    queryKey: ['own-rates-read', trunkId, ai?.id],
    queryFn: () => api.get<{ status: 'reading' | 'done' | 'failed'; how: 'direct' | 'ai' | null; csv: string | null; currency: string | null; notes: string[]; error: string | null; seconds: number }>(`${base}/read/${ai!.id}`),
    enabled: !!ai && ai.status === 'reading',
    refetchInterval: 2_000,
    refetchIntervalInBackground: true,
  });
  useEffect(() => {
    const j = job.data;
    if (!j || !ai || j.status === 'reading') { if (j && ai) setAi({ ...ai, seconds: j.seconds }); return; }
    setAi({ ...ai, status: j.status, how: j.how, notes: j.notes, currency: j.currency, error: j.error, seconds: j.seconds });
    if (j.status === 'done' && j.csv) setParsed(parseRateSheet(parseCsvRows(j.csv), '27'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.data]);
  useEffect(() => { if (job.error && ai) setAi({ ...ai, status: 'failed', error: 'The read was lost (the server restarted). Try again.' }); }, [job.error]); // eslint-disable-line react-hooks/exhaustive-deps

  const read = async (f: File) => {
    setFile(f.name); setBlob(f); setAi(null);
    if (/\.(csv|txt)$/i.test(f.name)) {
      const local = parseRateSheet(parseCsvRows(await f.text()), '27');
      if (!('error' in local) && local.rules.length > 0) { setParsed(local); return; }
    }
    await send(f);
  };
  const ok = parsed && !('error' in parsed) ? parsed : null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <strong style={{ fontSize: 13 }}>Carrier rates</strong>
        <span className="arc-muted" style={{ fontSize: 12.5, flex: '1 1 260px' }}>
          {sheet.isLoading ? '…' : current
            ? `${current.rule_count.toLocaleString()} destinations${current.file_name ? ` from ${current.file_name}` : ''}, since ${new Date(current.effective_from).toLocaleDateString()}. Calls over this trunk show what the carrier charges.`
            : 'No rate sheet: calls over this trunk show no cost. Upload your carrier’s sheet to see it per call. ArcNet does not charge these calls.'}
        </span>
        {mayWrite && (
          <label className="arc-btn arc-btn-sm" style={{ cursor: 'pointer' }}>
            {current ? 'Upload a new sheet' : 'Upload sheet'}
            <input type="file" accept=".csv,.txt,.xlsx,.xls,.pdf" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); e.target.value = ''; }} />
          </label>
        )}
        {mayWrite && current && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={remove.isPending}
            onClick={() => { void confirmAction({ title: 'Stop showing carrier costs for this trunk?', body: 'Calls already priced keep their cost.', action: 'Stop showing', danger: true }).then((ok) => { if (ok) remove.mutate(); }); }}>
            Remove
          </button>
        )}
      </div>
      {sheet.error && <ErrorBanner error={sheet.error} />}
      {remove.error && <ErrorBanner error={remove.error} />}

      {ai && ai.status === 'reading' && (
        <div className="arc-banner arc-banner-info" role="status" style={{ marginTop: 10 }}>
          <span style={{ flex: 1 }}>Reading {file}… a long PDF takes a minute or two.</span>
          <span className="arc-subtle arc-num" style={{ fontSize: 12 }}>{ai.seconds} s</span>
        </div>
      )}
      {ai && ai.status === 'failed' && (
        <div className="arc-banner arc-banner-danger" role="alert" style={{ marginTop: 10 }}>
          <span style={{ flex: 1 }}>{file} could not be read: {ai.error}</span>
          {blob && <button type="button" className="arc-btn arc-btn-sm" onClick={() => void send(blob)}>Try again</button>}
        </div>
      )}
      {parsed && 'error' in parsed && (
        <div className="arc-banner arc-banner-danger" role="alert" style={{ marginTop: 10 }}>
          <span style={{ flex: 1 }}>{file}: {parsed.error}</span>
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setParsed(null)}>Dismiss</button>
        </div>
      )}

      {ok && (
        <div className="arc-stack" style={{ gap: 8, marginTop: 10, padding: 12, border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r-sm)' }}>
          <div style={{ fontSize: 13 }}>
            <strong>{ok.rules.length.toLocaleString()}</strong> rates read from <span className="arc-mono">{file}</span>
            {ok.skipped > 0 && <>, <span style={{ color: 'var(--arc-warn)' }}>{ok.skipped} rows skipped</span></>}.
            <span className="arc-subtle"> Prefix from “{ok.columns.prefix}”, rate from “{ok.columns.rate}”
              {ok.columns.increments ? `, increments from “${ok.columns.increments}”` : ', per second'}.</span>
          </div>
          {ai?.how === 'ai' && (
            <div className="arc-banner arc-banner-warn" role="note">
              <span style={{ flex: 1 }}>
                Read by AI. Check a few rates against the sheet before using it.
                {ai.currency && ai.currency !== (current?.currency ?? 'ZAR') ? ` The sheet is in ${ai.currency}; calls are shown in ${current?.currency ?? 'ZAR'}.` : ''}
              </span>
            </div>
          )}
          {ai && ai.notes.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
              {ai.notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          )}
          {ok.problems.length > 0 && (
            <ul className="arc-subtle" style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
              {ok.problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead><tr><th>Prefix</th><th>Destination</th><th className="arc-td-right">A minute</th><th className="arc-td-right">Increments</th></tr></thead>
              <tbody>
                {ok.rules.slice(0, 6).map((r) => (
                  <tr key={r.prefix}>
                    <td className="arc-mono">{r.prefix}</td><td>{r.description ?? '—'}</td>
                    <td className="arc-td-right arc-num">{r.rate_per_minute}</td>
                    <td className="arc-td-right arc-num">{r.first_increment_sec}/{r.subsequent_increment_sec} s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {upload.error && <ErrorBanner error={upload.error} />}
          <div className="arc-row" style={{ gap: 8 }}>
            <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={ok.rules.length === 0 || upload.isPending}
              onClick={() => upload.mutate()}>
              {upload.isPending ? 'Saving…' : 'Use this sheet'}
            </button>
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => { setParsed(null); setFile(null); }}>Cancel</button>
            {ai?.how !== 'ai' && blob && (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => void send(blob, true)}
                title="If the columns above were read wrongly">
                Read it with AI instead
              </button>
            )}
          </div>
        </div>
      )}

      {current && (
        <div className="arc-row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <input className="arc-input arc-mono" style={{ maxWidth: 200 }} placeholder="082 123 4567" value={number}
            aria-label="Number to price" onChange={(e) => setNumber(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setAsked(number.trim()); }} />
          <button type="button" className="arc-btn arc-btn-sm" disabled={number.trim().length < 2} onClick={() => setAsked(number.trim())}>
            What does it cost?
          </button>
          {lookup.data && (
            <span className="arc-muted" style={{ fontSize: 12.5 }}>
              {lookup.data.found && lookup.data.rule
                ? <>{formatMoney(lookup.data.rule.rate_per_minute, lookup.data.currency ?? 'ZAR')} a minute
                    {lookup.data.rule.description ? ` (${lookup.data.rule.description}, ` : ' ('}prefix {lookup.data.rule.prefix})
                    {lookup.data.rule.first_increment_sec !== 1 || lookup.data.rule.subsequent_increment_sec !== 1
                      ? `, billed ${lookup.data.rule.first_increment_sec}/${lookup.data.rule.subsequent_increment_sec} s` : ', per second'}.</>
                : `No rate for ${lookup.data.number} on the sheet: such calls show no cost.`}
            </span>
          )}
          {lookup.error && <ErrorBanner error={lookup.error} />}
        </div>
      )}
    </div>
  );
}
